import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Stack,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import api from "../api/axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

/* ================= CHIP STYLE ================= */
const chipStyle = {
  px: 1.5,
  py: 0.5,
  borderRadius: "999px",
  fontSize: "0.75rem",
  fontWeight: 500,
  background: "rgba(255,255,255,0.1)",
  border: "1px solid rgba(255,255,255,0.2)",
};

/* ================= INFO COMPONENT ================= */
const Info = ({ label, value }) => (
  <Box
    sx={{
      minWidth: 140,
      flex: "1 1 140px",
    }}
  >
    <Typography variant="caption" sx={{ opacity: 0.6, display: "block" }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value || "-"}
    </Typography>
  </Box>
);

export default function FinanceProjectDetails() {
  const navigate = useNavigate();
  const { state: project } = useLocation();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({
    date: "",
    qty: "",
    amount: "",
    description: "",
  });

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        if (project?.payment_terms_verified !== 1) return;

        const { data } = await api.get(
          `/finance/get-payment-terms/${project.id}`,
        );

        const formattedRows = data.data.map((item, index) => ({
          id: index + 1,
          date: item.date,
          qty: item.qty,
          amount: item.amount,
          description: item.description,
        }));

        setRows(formattedRows);
      } catch (err) {
        console.error("Failed to fetch schedule", err);
      }
    };

    fetchSchedule();
  }, [project]);

  const handleSubmit = async () => {
    if (!rows.length) return;

    const confirmSubmit = window.confirm(
      `Total Work Order Value: ₹ ${totalAmount.toLocaleString()}\n\nDo you want to submit?`,
    );

    if (!confirmSubmit) return;

    try {
      const payload = {
        project_id: project.id,
        schedules: rows,
        total_amount: totalAmount,
      };

      //Fetch Payment Terms
      await api.post("/finance/save-payment-terms", payload);

      alert("Payment schedule submitted successfully ✅");
      // redirect to project list
      navigate("/finance/get-projects");
    } catch (err) {
      console.error(err);
      alert("Error submitting schedule ❌");
    }
  };

  /* ================= ADD ROW ================= */
  const handleAdd = () => {
    if (!form.date || !form.amount) return;
    setRows([
      ...rows,
      {
        id: rows.length + 1,
        ...form,
      },
    ]);
    setForm({
      date: "",
      qty: "",
      amount: "",
      description: "",
    });
  };

  /* ================= TOTAL ================= */
  const totalAmount = rows.reduce((sum, r) => sum + Number(r.amount || 0), 0);

  /* ================= COLUMNS ================= */
  const columns = [
    {
      field: "date",
      headerName: "Date",
      flex: 1,
    },
    { field: "qty", headerName: "Qty", flex: 1 },
    { field: "amount", headerName: "Amount (₹)", flex: 1 },
    { field: "description", headerName: "Description", flex: 2 },
  ];

  return (
    <Box p={3}>
      <Box mb={2}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            color: "white",
            textTransform: "none",
            backdropFilter: "blur(8px)",
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.12)",
            "&:hover": {
              background: "rgba(255,255,255,0.12)",
            },
          }}
        >
          Back
        </Button>
      </Box>
      {/* ================= HEADER ================= */}
      <Box
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 4,
          position: "relative",
          overflow: "hidden",
          backdropFilter: "blur(16px)",
          background:
            "linear-gradient(135deg, rgba(34,197,94,0.15), rgba(255,255,255,0.05))",
          border: "1px solid rgba(255,255,255,0.15)",
          boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
          color: "white",
        }}
      >
        {/* Glow */}
        <Box
          sx={{
            position: "absolute",
            top: "-50%",
            right: "-20%",
            width: 300,
            height: 300,
            background: "rgba(34,197,94,0.2)",
            filter: "blur(120px)",
            zIndex: 0,
          }}
        />

        <Box position="relative" zIndex={1}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Box>
              {/* Title */}
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ letterSpacing: "0.3px" }}
              >
                {project?.project_name}
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.65)", mb: 2 }}
              >
                {project?.customer}
              </Typography>
            </Box>
            <Box>
              {/* Chips */}
              <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                <Box sx={chipStyle}>{"BD - " + project?.bd_name}</Box>
                <Box sx={chipStyle}>{project?.department}</Box>
                <Box sx={chipStyle}>{project?.district}</Box>
              </Box>
            </Box>
          </Stack>

          <Divider
            sx={{
              mb: 2,
              borderColor: "rgba(255,255,255,0.08)",
            }}
          />

          {/* Compact Info (STACK) */}
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={3}
            justifyContent="space-between"
          >
            <Info label="POC Name" value={project?.pocName} />
            <Info label="POC WhatsApp" value={project?.pocWhatsapp} />
            <Info label="Work Order No" value={project?.workOrderNumber} />
            <Info
              label="WO Date"
              value={
                project?.workorder_date
                  ? dayjs(project.workorder_date).format("DD MMM YYYY")
                  : "-"
              }
            />
            <Info
              label="Deadline"
              value={
                project?.contractPeriod
                  ? dayjs(project.contractPeriod).format("DD MMM YYYY")
                  : "-"
              }
            />
            <Info
              label="Quantity"
              value={`${project?.qty || "-"} ${project?.unit || ""}`}
            />
            <Info
              label="Created On"
              value={
                project?.created_at
                  ? dayjs(project.created_at).format("DD MMM YYYY")
                  : "-"
              }
            />
          </Stack>
        </Box>
      </Box>

      {/* ================= FORM ================= */}
      {project?.payment_terms_verified === 1 && (
        <Box mb={2} p={2} borderRadius={2} bgcolor="rgba(34,197,94,0.15)">
          <Typography color="success.main" fontWeight={600}>
            Payment Terms Already Verified (Read Only Mode)
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 3,
          backdropFilter: "blur(10px)",
          background: "rgba(255,255,255,0.06)",
          opacity: project?.payment_terms_verified === 1 ? 0.5 : 1,
          pointerEvents:
            project?.payment_terms_verified === 1 ? "none" : "auto",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography fontWeight={600}>
            Add Tentative Payment Schedule
          </Typography>

          <Typography
            sx={{
              fontSize: "1.4rem",
              fontWeight: 700,
              color: "#22c55e",
              letterSpacing: "0.5px",
            }}
          >
            Total Work Order Value ₹ {totalAmount.toLocaleString()}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid xs={12} md={2}>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={form.date}
              onChange={(e) => {
                setForm({ ...form, date: e.target.value });
              }}
            />
          </Grid>

          <Grid xs={12} md={2}>
            <TextField
              label="Qty (optional)"
              size="small"
              fullWidth
              value={form.qty}
              onChange={(e) => {
                const value = e.target.value;
                // allow empty OR valid number
                if (value === "" || /^\d*\.?\d*$/.test(value)) {
                  setForm({ ...form, qty: value });
                }
              }}
            />
          </Grid>

          <Grid xs={12} md={3}>
            <TextField
              label="Amount (₹)"
              size="small"
              fullWidth
              value={form.amount}
              onChange={(e) => {
                const value = e.target.value;
                // allow empty OR valid number
                if (value === "" || /^\d*\.?\d*$/.test(value)) {
                  setForm({ ...form, amount: value });
                }
              }}
            />
          </Grid>

          <Grid xs={12} md={4}>
            <TextField
              label="Description (optional)"
              size="small"
              fullWidth
              value={form.description}
              onChange={(e) =>
                setForm({
                  ...form,
                  description: e.target.value,
                })
              }
            />
          </Grid>

          <Grid xs={12} md={1}>
            <Button variant="contained" fullWidth onClick={handleAdd}>
              Add
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* ================= DATAGRID ================= */}
      <Box
        sx={{
          height: 420,
          borderRadius: 3,
          backdropFilter: "blur(10px)",
          background: "rgba(255,255,255,0.05)",
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          pageSizeOptions={[5, 10, 20]}
          disableRowSelectionOnClick
          sx={{
            border: "none",
            color: "white",
            "& .MuiDataGrid-columnHeaders": {
              background: "rgba(34,197,94,0.2)",
              color: "#fff",
            },
          }}
        />

        <Box mt={2} display="flex" justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={
              project?.payment_terms_verified === 1 || rows.length === 0
            }
            sx={{
              px: 4,
              py: 1,
              fontWeight: 600,
              background:
                project?.payment_terms_verified === 1
                  ? "rgba(255,255,255,0.2)"
                  : "linear-gradient(135deg, #22c55e, #16a34a)",
            }}
          >
            Submit Payment Schedule
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
